import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';
import {
  ExclusivePriceSetup,
  ExclusivePriceSetupProps,
} from '@client/new/common/selfServeExclusive/exclusivePriceSetup';
import { StepCount } from './StepCount';
import { clsx } from 'clsx';
import { isEmpty } from 'lodash';
import { Alert } from '@design-system/components/alert/alert';
import { ExternalLink } from '@design-system/components/link/externalLink';

export const SubscriptionPriceSelection = ({
  next,
  back,
  savePricing,
  max,
  step,
  errors,
  ...setup
}: {
  next: () => void;
  back?: () => void;
  savePricing: (callback?: () => void) => void;
  max: number;
  step: number;
  errors?: string[];
} & ExclusivePriceSetupProps) => {
  return (
    <div className="w-full h-full flex flex-col">
      <StepCount step={step} max={max} />
      <div className="flex flex-col items-center justify-start md:justify-center gap-[23px] w-full h-[100vh] overflow-y-scroll md:overflow-y-auto">
        <Typography variant="headline" size="large" className="text-center">
          Subscription price
        </Typography>
        <div className="flex flex-col gap-1 xl:gap-4 w-full md:w-[860px]">
          {isEmpty(errors) ? null : (
            <Alert
              variant="error"
              title="Can’t save, required fields or information are missing"
              details={errors}
              allowClose={true}
            />
          )}
          <div
            className={clsx(
              'flex flex-col md:flex-row p-8 w-full gap-12',
              sprinkles({ bg: 'N-10' }),
            )}
          >
            <ExclusivePriceSetup {...setup} sectionClassName="p-0" />
            <div className="flex flex-col gap-4">
              <div
                className={clsx(
                  'flex flex-col rounded-[10px] h-max w-full',
                  sprinkles({ bg: 'B-light' }),
                )}
              >
                <Alert
                  variant="info"
                  title="Price changes"
                  details={[
                    'You can change your pricing in Subscription’s Settings up until you go live.',
                    '🚨 After that, you will not be able to change them. 🚨',
                  ]}
                />
                <Alert
                  variant="info"
                  title="Discounts"
                  details='After you go live, you will be able to apply discounts in "Promotions" tab.'
                />
              </div>
              <PriceGuideInfo />
            </div>
          </div>
        </div>
      </div>
      <div
        className={clsx(
          'flex',
          back && 'justify-between',
          !back && 'justify-center',
        )}
      >
        {back && (
          <Button variant="text" color="neutral" onClick={back}>
            Back
          </Button>
        )}
        <Button
          color="neutral"
          onClick={() => savePricing(next)}
          disabled={setup.disabled || setup.submitting}
          loading={setup.submitting}
        >
          Save & Proceed
        </Button>
      </div>
    </div>
  );
};

const guides = [
  {
    href: 'https://www.loom.com/share/011658005a5c4cb0ad3cd6fb6792b95c?sid=13cbfcb5-c2ae-4a7f-adbc-aaa8f2e3c0eb',
    label: 'Watch this',
    text: 'quick video on pricing.',
  },
  {
    href: 'https://www.loom.com/share/c4c7a34adacb43d3bf918e52a24ce0e5?sid=3b311972-5e7d-4d6b-99c1-7aa8db03f52c',
    label: 'Watch this',
    text: 'quick video on subscription best practices.',
  },
  {
    href: 'https://help.playbookapp.io/en/articles/9698798-exclusive-subscription-strategy-pricing-and-packaging',
    label: 'Read up',
    text: 'on our best pricing practices for your subscription.',
  },
];

const PriceGuideInfo = () => (
  <div className="flex flex-col gap-2 p-4">
    <Typography variant="body" size="medium">
      How to price a subscription?
    </Typography>
    <Typography variant="body" size="medium" fontWeight="normal">
      Pricing is specific to you, the amount of effort you’ll be putting into
      your offering.
    </Typography>
    {guides.map(({ href, label, text }) => (
      <div className="flex items-center gap-1" key={href}>
        <ExternalLink href={href} label={label} textColor="P-80" />
        <Typography variant="body" size="medium" fontWeight="normal">
          {text}
        </Typography>
      </div>
    ))}
  </div>
);
